import { Component, computed, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    FormControlStatus,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validators
} from "@angular/forms";
import { filter, map, pairwise, startWith } from "rxjs/operators";
import { SensorEventActuator, SensorEventActuatorType } from "../../ParseClass/SensorEvent.Parse";
import {
    arrayIsSet,
    deepClone,
    GlobalVariablesService,
    isNotNullOrUndefined, objectIsSet,
    stringIsSet
} from "../../providers/service/global-variables.service";
import { combineLatest, Observable, Subject, Subscription } from "rxjs";
import { ElementParse } from "../../ParseClass/Element.Parse";
import { dataForm, GroupDataForm } from "../auto-complete-form/auto-complete-form.component";
import { Light, LightStatus, LightType } from "../../ParseClass/light";
import { Shutter, ShutterStatus, ShutterType } from "../../ParseClass/shutter";
import { Switch, SwitchStatus } from "../../ParseClass/switch";
import { Command } from "../../ParseClass/command";
import { LinearActuator } from "../../ParseClass/linear-actuator";
import { TranslateService } from '@ngx-translate/core';
import { E } from '@angular/cdk/keycodes';
import { SensorDataParse } from 'src/app/ParseClass/SensorData.Parse';

enum PointsTypeValues {
    FIXED,
    FROM_SENSOR
}

@Component({
    selector: 'app-form-field-actuators-sensor-event',
    templateUrl: './form-field-actuators-sensor-event.component.html',
    styleUrls: ['./form-field-actuators-sensor-event.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FormFieldActuatorsSensorEventComponent
        },
    ],
    standalone: false
})
export class FormFieldActuatorsSensorEventComponent implements OnInit, OnDestroy, ControlValueAccessor {
    public form: FormGroup
    private subscriptions = new Subscription();
    @Input() elements: ElementParse[] | undefined;
    @Input() lights: Light[] | undefined;
    @Input() shutters: Shutter[] | undefined;
    @Input() switches: Switch[] | undefined;
    @Input() commands: Command[] | undefined;
    @Input() linearActuators: LinearActuator[] | undefined;
    @Input() sensorDataTypes: dataForm[] | undefined;
    @Output() statusChange = new Subject<FormControlStatus>();
    actuatorType = SensorEventActuatorType
    type$: Observable<SensorEventActuatorType | undefined>
    sourceSensor$: Observable<ElementParse | undefined>
    sourceSensorX$: Observable<ElementParse | undefined>
    sourceSensorP1X$: Observable<ElementParse | undefined>
    sourceSensorP1Y$: Observable<ElementParse | undefined>
    sourceSensorP2X$: Observable<ElementParse | undefined>
    sourceSensorP2Y$: Observable<ElementParse | undefined>
    targetElement$: Observable<ElementParse | undefined>
    light$: Observable<ElementParse | undefined>
    shutter$: Observable<ElementParse | undefined>
    command$: Observable<Command | undefined>
    commandLinear$: Observable<Command | undefined>
    possibleCommandInCommand$: Observable<any | undefined>
    commandValueType$: Observable<any | undefined>
    shutterLevelTraduction$: Observable<{ level: number } | undefined>
    shutterStatusTraduction$: Observable<string>

    lightLevelTraduction$: Observable<{ level: number } | undefined>
    lightStatusTraduction$: Observable<string>
    relayIsOnTraduction$: Observable<string>
    public typeLight = LightType
    public typeShutter = ShutterType
    possibleCommandInCommandType = {
        default: "Default",
        bool: "bool"
    }

    p1Type$: Observable<PointsTypeValues>
    p2Type$: Observable<PointsTypeValues>
    valueTypeLinear = signal<any | undefined>(undefined)
    minValueLinear = computed(() => {
        const values = this.valueTypeLinear()
        const min = values?.minVal
        return min != null ? min : undefined
    })
    maxValueLinear = computed(() => {
        const values = this.valueTypeLinear()
        const max = values?.maxVal
        return max != null ? max : undefined
    })
    xValueLinear = computed(() => {
        const values = this.valueTypeLinear()
        const infoName = this.globalVariablesService.instantSensorDataTypeTraduction(values?.x_infoName, this.ts.currentLang) ?? values?.x_infoName
        const sourceId = this.getElementName(values?.x_sourceId)
        const value = { sx: sourceId, dx: infoName }
        return value
    })
    commandValueLinear = computed(() => {
        const values = this.valueTypeLinear()
        const sensorId = this.getElementName(values?.objectId)
        const trad = this.ts.instant('possibleCommands.' + values?.command)
        const infoName = trad.includes('possibleCommands.') ? values?.command : trad
        const value = { sx: sensorId, dx: infoName }
        return value
    })
    p1ValueLinear = computed(() => {
        const values = this.valueTypeLinear()
        const p1 = values?.p1
        let x
        let y
        if (p1) {
            x = { sx: undefined, dx: undefined }
            y = { sx: undefined, dx: undefined }
            if (p1.value) {
                x.sx = p1.value.x
                y.sx = p1.value.y
            }
            if (p1.source) {
                x.sx = this.getElementName(p1.source.x.sensorId)
                x.dx = this.globalVariablesService.instantSensorDataTypeTraduction(p1.source.x.infoName, this.ts.currentLang) ?? p1.source.x.infoName
                y.sx = this.getElementName(p1.source.y.sensorId)
                y.dx = this.globalVariablesService.instantSensorDataTypeTraduction(p1.source.y.infoName, this.ts.currentLang) ?? p1.source.y.infoName
            }
        }
        return p1 ? { x, y } : undefined
    })
    p2ValueLinear = computed(() => {
        const values = this.valueTypeLinear()
        const p2 = values?.p2
        let x
        let y
        if (p2) {
            x = { sx: undefined, dx: undefined }
            y = { sx: undefined, dx: undefined }
            if (p2.value) {
                x.sx = p2.value.x
                y.sx = p2.value.y
            }
            if (p2.source) {
                x.sx = this.getElementName(p2.source.x.sensorId)
                x.dx = this.globalVariablesService.instantSensorDataTypeTraduction(p2.source.x.infoName, this.ts.currentLang) ?? p2.source.x.infoName
                y.sx = this.getElementName(p2.source.y.sensorId)
                y.dx = this.globalVariablesService.instantSensorDataTypeTraduction(p2.source.y.infoName, this.ts.currentLang) ?? p2.source.y.infoName
            }
        }
        return p2 ? { x, y } : undefined
    })

    constructor(fb: FormBuilder,
        private globalVariablesService: GlobalVariablesService,
        private ts: TranslateService
    ) {
        const formGroupP = () => ({
            type: PointsTypeValues.FIXED,
            value: fb.group({ x: undefined, y: undefined }),
            source: fb.group({
                x: fb.group({ sensorId: undefined, infoName: undefined }),
                y: fb.group({ sensorId: undefined, infoName: undefined }),
            })
        })

        this.form = fb.group({
            type: [undefined, [Validators.required]],
            configTypeEXT_SENSOR: fb.group({
                sourceSensorId: [undefined, [Validators.required]],
                sourceInfoName: [undefined, [Validators.required]],
                targetElementId: [undefined, [Validators.required]],
                targetCommandName: [undefined, [Validators.required]]
            }),
            configTypeRELAY: fb.group({
                objectId: [undefined, [Validators.required]],
                status: [false, [Validators.required]]
            }),
            configTypeLIGHT: fb.group({ objectId: [undefined, [Validators.required]], status: false, level: 50 }),
            configTypeSHUTTER: fb.group({ objectId: [undefined, [Validators.required]], status: false, level: 50 }),
            configTypeCOMMAND: fb.group({
                objectId: [undefined, [Validators.required]],
                command: undefined,
                value: undefined
            }),
            configTypeLINEAR: fb.group({
                objectId: undefined,
                command: undefined,
                x_sourceId: undefined,
                x_infoName: undefined,
                minVal: undefined,
                maxVal: undefined,
                p1: fb.group(formGroupP()),
                p2: fb.group(formGroupP()),
            }),
        })
        this.p1Type$ = this.form.get('configTypeLINEAR')!.get('p1')!.get('type')!.valueChanges.pipe(
            startWith(undefined),
            map(() => this.form.get('configTypeLINEAR')?.get('p1')?.get('type')!.value)
        )
        this.p2Type$ = this.form.get('configTypeLINEAR')!.get('p2')!.get('type')!.valueChanges.pipe(
            startWith(undefined),
            map(() => this.form.get('configTypeLINEAR')?.get('p2')?.get('type')!.value)
        )
        this.subscriptions.add(
            this.form.statusChanges.pipe(
                startWith(undefined),
                map(() => this.form.status)
            )
                .subscribe(status => {
                    setTimeout(() => {
                        this.statusChange.next(status)
                    })

                })
        )
        this.subscriptions.add(
            this.form.get('configTypeLINEAR')?.valueChanges.pipe(
                startWith(undefined),
                map(() => this.form.get('configTypeLINEAR')?.value)
            )
                .subscribe(value => {
                    let enabled = this.form.get('configTypeLINEAR')?.enabled
                    if (enabled) {
                        const r = this.validateLinearActuatorConfig(value);
                        if (!r.isValid) {
                            this.form.get('configTypeLINEAR')?.setErrors({ invalid: true })
                        } else {
                            this.form.get('configTypeLINEAR')?.setErrors(null)
                        }
                    }
                })
        )
        setTimeout(() => {
            this.statusChange.next(this.form.status)
        })
        this.targetElement$ = (this.form.get('configTypeEXT_SENSOR')?.get('targetElementId') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const objectId = this.form.get('configTypeEXT_SENSOR')?.get('targetElementId')?.value;
                if (arrayIsSet(this.elements) && stringIsSet(objectId)) {
                    return this.elements.find(el => el.objectId === objectId)
                } else {
                    return undefined;
                }
            })
        )
        this.relayIsOnTraduction$ = (this.form.get('configTypeRELAY')?.get('status') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const isOn = this.form.get('configTypeRELAY')?.get('status')?.value;
                if (isOn == true) {
                    return 'SensorEvent.actived';
                } else if (isOn == false) {
                    return 'SensorEvent.notActived';
                } else {
                    return 'SensorEvent.actived_notActived';
                }
            })
        )
        this.sourceSensor$ = (this.form.get('configTypeEXT_SENSOR')?.get('sourceSensorId') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const objectId = this.form.get('configTypeEXT_SENSOR')?.get('sourceSensorId')?.value;
                if (arrayIsSet(this.elements) && stringIsSet(objectId)) {
                    return this.elements.find(el => el.objectId === objectId)
                } else {
                    return undefined;
                }
            })
        )
        this.light$ = (this.form.get('configTypeLIGHT')?.get('objectId') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const objectId = this.form.get('configTypeLIGHT')?.get('objectId')?.value;
                if (arrayIsSet(this.lights) && stringIsSet(objectId)) {
                    return this.lights.find(el => el.objectId === objectId)
                } else {
                    return undefined;
                }
            })
        )
        this.lightLevelTraduction$ = (this.form.get('configTypeLIGHT')?.get('level') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const level = this.form.get('configTypeLIGHT')?.get('level')?.value;
                if (level != null) {
                    return { level }
                } else {
                    return { level: '' }
                }
            })
        )
        this.lightStatusTraduction$ = (this.form.get('configTypeLIGHT')?.get('status') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const level = this.form.get('configTypeLIGHT')?.get('status')?.value;
                if (level != null) {
                    return 'SensorEvent.light.status.' + (level === true ? LightStatus.ON : LightStatus.OFF)
                } else {
                    return 'SensorEvent.light.status.undefined'
                }
            })
        )
        this.shutter$ = (this.form.get('configTypeSHUTTER')?.get('objectId') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const objectId = this.form.get('configTypeSHUTTER')?.get('objectId')?.value;
                if (arrayIsSet(this.shutters) && stringIsSet(objectId)) {
                    return this.shutters.find(el => el.objectId === objectId)
                } else {
                    return undefined;
                }
            })
        )
        this.shutterLevelTraduction$ = (this.form.get('configTypeSHUTTER')?.get('level') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const level = this.form.get('configTypeSHUTTER')?.get('level')?.value;
                if (level != null) {
                    return { level }
                } else {
                    return { level: '' }
                }
            })
        )
        this.shutterStatusTraduction$ = (this.form.get('configTypeSHUTTER')?.get('status') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const level = this.form.get('configTypeSHUTTER')?.get('status')?.value;
                if (level != null) {
                    return 'SensorEvent.shutter.status.' + (level === true ? ShutterStatus.UP : ShutterStatus.DOWN)
                } else {
                    return 'SensorEvent.shutter.status.undefined'
                }
            })
        )
        this.command$ = (this.form.get('configTypeCOMMAND')?.get('objectId') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const objectId = this.form.get('configTypeCOMMAND')?.get('objectId')?.value;
                if (arrayIsSet(this.commands) && stringIsSet(objectId)) {
                    return this.commands.find(el => el.objectId === objectId)
                } else {
                    return undefined;
                }
            })
        )
        this.possibleCommandInCommand$ = (this.form.get('configTypeCOMMAND')?.get('command') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                return this.form.get('configTypeCOMMAND')?.get('command')?.value;
            })
        )

        this.commandValueType$ = combineLatest([this.command$, this.possibleCommandInCommand$]).pipe(
            map(([command, possibleCommand]) => {
                if (possibleCommand && command && command.possibleCommands && command.possibleCommands[possibleCommand]) {
                    if (command.possibleCommands[possibleCommand].dataType == 'BOOL') {
                        return this.possibleCommandInCommandType.bool
                    }
                }
                return this.possibleCommandInCommandType.default
            })
        )

        this.commandLinear$ = (this.form.get('configTypeLINEAR')?.get('objectId') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const objectId = this.form.get('configTypeLINEAR')?.get('objectId')?.value;
                if (arrayIsSet(this.elements) && stringIsSet(objectId)) {
                    return this.elements.find(el => el.objectId === objectId)
                } else {
                    return undefined;
                }
            })
        )

        this.type$ = this.form.valueChanges.pipe(
            startWith(undefined),
            map(() => this.form.get('type')?.value)
        )
        this.sourceSensorX$ = (this.form.get('configTypeLINEAR')?.get('x_sourceId') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const objectId = this.form.get('configTypeLINEAR')?.get('x_sourceId')?.value;
                if (arrayIsSet(this.elements) && stringIsSet(objectId)) {
                    return this.elements.find(el => el.objectId === objectId)
                } else {
                    return undefined;
                }
            })
        )
        this.sourceSensorP1X$ = (this.form.get('configTypeLINEAR')?.get('p1')?.get('source')?.get('x')?.get('sensorId') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const objectId = this.form.get('configTypeLINEAR')?.get('p1')?.get('source')?.get('x')?.get('sensorId')?.value;
                if (arrayIsSet(this.elements) && stringIsSet(objectId)) {
                    return this.elements.find(el => el.objectId === objectId)
                } else {
                    return undefined;
                }
            })
        )
        this.sourceSensorP1Y$ = (this.form.get('configTypeLINEAR')?.get('p1')?.get('source')?.get('y')?.get('sensorId') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const objectId = this.form.get('configTypeLINEAR')?.get('p1')?.get('source')?.get('y')?.get('sensorId')?.value;
                if (arrayIsSet(this.elements) && stringIsSet(objectId)) {
                    return this.elements.find(el => el.objectId === objectId)
                } else {
                    return undefined;
                }
            })
        )
        this.sourceSensorP2X$ = (this.form.get('configTypeLINEAR')?.get('p2')?.get('source')?.get('x')?.get('sensorId') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const objectId = this.form.get('configTypeLINEAR')?.get('p2')?.get('source')?.get('x')?.get('sensorId')?.value;
                if (arrayIsSet(this.elements) && stringIsSet(objectId)) {
                    return this.elements.find(el => el.objectId === objectId)
                } else {
                    return undefined;
                }
            })
        )
        this.sourceSensorP2Y$ = (this.form.get('configTypeLINEAR')?.get('p2')?.get('source')?.get('y')?.get('sensorId') as any).valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const objectId = this.form.get('configTypeLINEAR')?.get('p2')?.get('source')?.get('y')?.get('sensorId')?.value;
                if (arrayIsSet(this.elements) && stringIsSet(objectId)) {
                    return this.elements.find(el => el.objectId === objectId)
                } else {
                    return undefined;
                }
            })
        )
        this.subscriptions.add(
            this.type$.pipe(
                pairwise(),
                filter(([prev, current]) => {
                    return prev != current;
                }),
                map(([prev, current]) => current)
            )
                .subscribe(type => {
                    if (type === this.actuatorType.EXT_SENSOR) {
                        this.form.get('configTypeEXT_SENSOR')?.enable();
                        this.form.get('configTypeRELAY')?.disable();
                        this.form.get('configTypeLIGHT')?.disable();
                        this.form.get('configTypeSHUTTER')?.disable();
                        this.form.get('configTypeCOMMAND')?.disable();
                        this.form.get('configTypeLINEAR')?.disable();
                    } else if (type === this.actuatorType.RELAY) {
                        this.form.get('configTypeEXT_SENSOR')?.disable();
                        this.form.get('configTypeRELAY')?.enable();
                        this.form.get('configTypeLIGHT')?.disable();
                        this.form.get('configTypeSHUTTER')?.disable();
                        this.form.get('configTypeCOMMAND')!.disable();
                        this.form.get('configTypeLINEAR')!.disable();
                        Object.keys(this.form.value).forEach(key => {
                            if (this.form.get(key)?.invalid) {
                                this.form.get(key)?.updateValueAndValidity()
                            }
                        })
                    } else if (type === this.actuatorType.LIGHT) {
                        this.form.get('configTypeEXT_SENSOR')?.disable();
                        this.form.get('configTypeRELAY')?.disable();
                        this.form.get('configTypeLIGHT')?.enable();
                        this.form.get('configTypeSHUTTER')?.disable();
                        this.form.get('configTypeCOMMAND')?.disable();
                        this.form.get('configTypeLINEAR')?.disable();
                    } else if (type === this.actuatorType.SHUTTER) {
                        this.form.get('configTypeEXT_SENSOR')?.disable();
                        this.form.get('configTypeRELAY')?.disable();
                        this.form.get('configTypeLIGHT')?.disable();
                        this.form.get('configTypeSHUTTER')?.enable();
                        this.form.get('configTypeCOMMAND')?.disable();
                        this.form.get('configTypeLINEAR')!.disable();
                    } else if (type === this.actuatorType.COMMAND) {
                        this.form.get('configTypeEXT_SENSOR')?.disable();
                        this.form.get('configTypeEXT_SENSOR')?.reset();
                        this.form.get('configTypeRELAY')?.disable();
                        this.form.get('configTypeLIGHT')?.disable();
                        this.form.get('configTypeSHUTTER')?.disable();
                        this.form.get('configTypeCOMMAND')?.enable();
                        this.form.get('configTypeLINEAR')?.disable();
                    } else if (type === this.actuatorType.LINEAR_ACTUATOR) {
                        this.form.get('configTypeEXT_SENSOR')?.disable();
                        this.form.get('configTypeEXT_SENSOR')?.reset();
                        this.form.get('configTypeRELAY')?.disable();
                        this.form.get('configTypeLIGHT')?.disable();
                        this.form.get('configTypeSHUTTER')?.disable();
                        this.form.get('configTypeCOMMAND')?.disable();
                        this.form.get('configTypeLINEAR')?.enable();
                    }
                    if (type !== this.actuatorType.LINEAR_ACTUATOR) {
                        this.form.get('configTypeLINEAR')?.disable();
                    }
                })
        )
        this.form.get('type')?.setValue(this.actuatorType.EXT_SENSOR);

    }

    ngOnInit(): void {
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    onTouched: Function = () => {
    };

    registerOnChange(onChange: any) {
        const values$ = this.form.valueChanges.pipe(
            startWith(undefined),
            map(() => {
                const values = this.form.value;
                if (values != null) {
                    const obj = deepClone(values)
                    const type = obj.type;
                    let config: any;
                    if (type === SensorEventActuatorType.EXT_SENSOR) {
                        config = obj.configTypeEXT_SENSOR;
                    } else if (type === SensorEventActuatorType.RELAY) {
                        config = obj.configTypeRELAY;

                        if (config != null && stringIsSet(config.objectId)) {
                            const switch_ = this.switches?.find(el => el.objectId == config.objectId)
                            if (switch_ != null) {
                                config['objectId'] = switch_.objectId;
                            }
                            if (config.status == true) {
                                config.status = SwitchStatus.ON;
                            } else {
                                config.status = SwitchStatus.OFF;
                            }
                        }
                    } else if (type === SensorEventActuatorType.LIGHT) {
                        config = obj.configTypeLIGHT;
                        if (config != null && stringIsSet(config.objectId)) {
                            const light = this.lights?.find(el => el.objectId == config.objectId)
                            if (light != null) {
                                if (light.type === LightType.RANGE) {
                                    delete config.status
                                    config['type'] = LightType.RANGE
                                } else if (light.type === LightType.BINARY) {
                                    delete config.level
                                    config['type'] = LightType.BINARY
                                    config.status = config.status == true ? LightStatus.ON : LightStatus.OFF
                                }
                            }
                        }

                    } else if (type === SensorEventActuatorType.SHUTTER) {
                        config = obj.configTypeSHUTTER;
                        if (config != null && stringIsSet(config.objectId)) {
                            const shutter: Shutter | undefined = this.shutters?.find(el => el.objectId == config.objectId)
                            if (shutter != null) {
                                if (shutter.type === ShutterType.RANGE) {
                                    delete config.status
                                    config['type'] = ShutterType.RANGE;
                                } else if (shutter.type === ShutterType.BINARY) {
                                    config['type'] = ShutterType.BINARY
                                    config.status = config.status == true ? ShutterStatus.UP : ShutterStatus.DOWN
                                    delete config.level
                                }
                            }
                        }
                    } else if (type === SensorEventActuatorType.COMMAND) {
                        config = obj.configTypeCOMMAND;
                    } else if (type === SensorEventActuatorType.LINEAR_ACTUATOR) {
                        const configLinear = deepClone(obj.configTypeLINEAR)
                        const p1 = deepClone(configLinear?.p1)
                        if (p1 && p1.type == PointsTypeValues.FIXED) {
                            configLinear.p1 = { value: p1.value }
                        } else if (p1) {
                            configLinear.p1 = { source: p1.source }
                        }
                        const p2 = deepClone(configLinear?.p2)
                        if (p2 && p2.type == PointsTypeValues.FIXED) {
                            configLinear.p2 = { value: p2.value }
                        } else if (p2) {
                            configLinear.p2 = { source: p2.source }
                        }
                        config = configLinear;
                        this.valueTypeLinear.set(configLinear);
                    }
                    delete obj.configTypeEXT_SENSOR
                    delete obj.configTypeRELAY
                    delete obj.configTypeLIGHT
                    delete obj.configTypeSHUTTER
                    delete obj.configTypeCOMMAND
                    delete obj.configTypeLINEAR
                    obj['config'] = config;
                    return obj;
                } else {
                    return;
                }
            })
        )
        this.subscriptions.add(
            values$.subscribe(onChange)
        );

    }

    setDisabledState(disabled: boolean) {
        if (disabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }
    }

    writeValue(value: SensorEventActuator) {
        if (isNotNullOrUndefined(value)) {
            const specialKeys = ['config'];
            Object.keys(value)
                .filter(key => !specialKeys.includes(key))
                .forEach((key) => {
                    if (this.form.get(key) != null) {
                        this.form.get(key)?.setValue((value as any)[key])
                    }
                })
            const config: any = value.config
            if (config != null) {
                let keyToSet: string;
                if (value.type == SensorEventActuatorType.LIGHT) {
                    keyToSet = 'configTypeLIGHT';
                } else if (value.type == SensorEventActuatorType.SHUTTER) {
                    keyToSet = 'configTypeSHUTTER';
                } else if (value.type == SensorEventActuatorType.RELAY) {
                    keyToSet = 'configTypeRELAY'
                } else if (value.type == SensorEventActuatorType.EXT_SENSOR) {
                    keyToSet = 'configTypeEXT_SENSOR';
                } else if (value.type == SensorEventActuatorType.COMMAND) {
                    keyToSet = 'configTypeCOMMAND';
                } else if (value.type == SensorEventActuatorType.LINEAR_ACTUATOR) {
                    keyToSet = 'configTypeLINEAR';
                } else {
                    keyToSet = '';
                }
                if (stringIsSet(keyToSet)) {
                    ['configTypeLIGHT', 'configTypeSHUTTER', 'configTypeRELAY', 'configTypeEXT_SENSOR', 'configTypeCOMMAND', 'configTypeLINEAR']
                        .filter(key => key != keyToSet)
                        .forEach(key => this.form.get(key)?.disable())
                    this.form.get(keyToSet)?.enable();

                    if (keyToSet == 'configTypeLINEAR') {
                        // Processa ricorsivamente il config per impostare i valori nel form
                        this.processObjectRecursively(config, (key, value, path) => {
                            // Solo per i valori primitivi (non oggetti)
                            if (typeof value !== 'object' || value === null) {
                                const pathArray = path.split('.');

                                // Verifica se il form control esiste per questo percorso
                                const formControl = this.getFormControlByPath(keyToSet, pathArray);
                                if (formControl) {
                                    formControl.setValue(value);
                                }
                            }
                        });

                        // Gestione speciale per impostare il tipo di P1 e P2
                        if (config.p1) {
                            const p1TypeControl = this.form.get('configTypeLINEAR')?.get('p1')?.get('type');
                            if (p1TypeControl) {
                                if (config.p1.source) {
                                    p1TypeControl.setValue(PointsTypeValues.FROM_SENSOR);
                                } else if (config.p1.value) {
                                    p1TypeControl.setValue(PointsTypeValues.FIXED);
                                }
                            }
                        }

                        if (config.p2) {
                            const p2TypeControl = this.form.get('configTypeLINEAR')?.get('p2')?.get('type');
                            if (p2TypeControl) {
                                if (config.p2.source) {
                                    p2TypeControl.setValue(PointsTypeValues.FROM_SENSOR);
                                } else if (config.p2.value) {
                                    p2TypeControl.setValue(PointsTypeValues.FIXED);
                                }
                            }
                        }

                    } else {
                        // Per gli altri tipi, usa il metodo originale
                        Object.keys(config).forEach(keyConf => {
                            if (this.form.get(keyToSet)?.get(keyConf) != null) {
                                this.form.get(keyToSet)?.get(keyConf)?.setValue(config[keyConf]);
                            }
                        });
                    }
                }
                this.form.get('type')?.reset()
                setTimeout(() => this.form.get('type')?.setValue(value.type), 20)
            }
        } else {
            this.form.reset()
        }
    }

    validate(control: AbstractControl): ValidationErrors | null {
        // if (!isNotNullOrUndefined(this.form.get('autoComplete')?.validator)) {
        //     this.form.get('autoComplete')?.setValidators(control.validator)
        //     this.form.get('autoComplete')?.updateValueAndValidity()
        // }
        if (this.form.valid) {
            return null
        } else {
            return this.form.errors!;
        }
    }

    addControlErrors(allErrors: any, controlName: string) {
        const errors = { ...allErrors };
        const controlErrors = this.form.controls[controlName].errors;
        if (controlErrors) {
            errors[controlName] = controlErrors;
        }
        return errors;
    }

    registerOnTouched(onTouched: Function) {
        this.onTouched = onTouched;
    }

    predicatePossibleValuesSensorElements(elements: ElementParse[], suggestedTrad: string, otherTrad: string): GroupDataForm[] | undefined {
        if (arrayIsSet(elements)) {
            let groups: GroupDataForm[] = []
            const suggested = elements
                .filter(el => el.type != null && el.type.periodicData != null && objectIsSet(el.type.periodicData))
            const other = elements
                .filter(el => el.type == null || el.type.periodicData == null || !objectIsSet(el.type.periodicData))
            if (arrayIsSet(suggested)) {
                groups.push({
                    groupName: suggestedTrad,
                    possibleValues: suggested.map(el => {
                        const obj: dataForm = {
                            valueForm: el.objectId!,
                            html: el.objectId!,
                            traduction: el.name,
                        }
                        return obj
                    })
                });
            }
            if (arrayIsSet(other)) {
                groups.push({
                    groupName: otherTrad,
                    possibleValues: other.map(el => {
                        const obj: dataForm = {
                            valueForm: el.objectId!,
                            html: el.objectId!,
                            traduction: el.name,
                        }
                        return obj
                    })
                });
            }

            return groups
        }
        return undefined
    }

    predicatePossibleValuesSensorElementsTypeRelay(elements: ElementParse[]): dataForm[] | undefined {
        if (arrayIsSet(elements)) {
            return elements
                .map(device => {
                    const pValue: dataForm = {
                        valueForm: device.objectId!,
                        html: device.objectId!,
                        traduction: device.name
                    }
                    return pValue;
                })
        } else {
            return undefined
        }
    }

    predicatePossibleValuesSensorElementsTypeLight(elements: ElementParse[]): dataForm[] | undefined {
        if (arrayIsSet(elements)) {
            return elements
                .map(device => {
                    const pValue: dataForm = {
                        valueForm: device.objectId!,
                        html: device.objectId!,
                        traduction: device.name
                    }
                    return pValue;
                })
        } else {
            return undefined
        }
    }

    predicateGetTypeLight(elementLight: Light): any | undefined {
        if (elementLight == null || elementLight.type == null) {
            return undefined
        } else {
            // switchcase not work with zero value
            return elementLight.type.toString();
        }

    }

    predicateGetTypeShutter(elementShutter: Shutter): undefined | any {
        if (elementShutter == null || elementShutter.type == null) {
            return undefined
        } else {
            // switchcase not work with zero value
            return elementShutter.type.toString()
        }
    }


    predicatePossibleValuesSensorElementsTypeShutter(elements: ElementParse[]): dataForm[] | undefined {
        if (arrayIsSet(elements)) {
            return elements
                .map(device => {
                    const pValue: dataForm = {
                        valueForm: device.objectId!,
                        html: device.objectId!,
                        traduction: device.name
                    }
                    return pValue;
                })
        } else {
            return undefined
        }
    }

    predicatePossibleValuesSensorElementsTypeCommand(elements: ElementParse[]): dataForm[] | undefined {
        if (arrayIsSet(elements)) {
            return elements
                .map(device => {
                    const pValue: dataForm = {
                        valueForm: device.objectId!,
                        html: device.objectId!,
                        traduction: device.name
                    }
                    return pValue;
                })
        } else {
            return undefined
        }
    }

    predicatePossibleValuesTargetElements(elements: ElementParse[], suggestedTrad: string, otherTrad: string): GroupDataForm[] | undefined {
        if (arrayIsSet(elements)) {
            let groups: GroupDataForm[] = []
            const suggested = elements
                .filter(el => el.type != null && el.type.possibleCommands != null && arrayIsSet(Object.keys(el.type.possibleCommands)))
            const other = elements
                .filter(el => el.type == null || el.type.possibleCommands == null || !arrayIsSet(Object.keys(el.type.possibleCommands)))
            if (arrayIsSet(suggested)) {
                groups.push({
                    groupName: suggestedTrad,
                    possibleValues: suggested.map(el => {
                        const obj: dataForm = {
                            valueForm: el.objectId!,
                            html: el.objectId!,
                            traduction: el.name,
                        }
                        return obj
                    })
                });
            }
            if (arrayIsSet(other)) {
                groups.push({
                    groupName: otherTrad,
                    possibleValues: other.map(el => {
                        const obj: dataForm = {
                            valueForm: el.objectId!,
                            html: el.objectId!,
                            traduction: el.name,
                        }
                        return obj
                    })
                });
            }
            return groups
        }
        return undefined
    }

    predicatePossibleValuesCommands(elements: Command[]): dataForm[] | undefined {
        if (arrayIsSet(elements)) {
            return elements
                .filter(el => el.possibleCommands != null && arrayIsSet(Object.keys(el.possibleCommands)))
                .map(device => {
                    const pValue: dataForm = {
                        valueForm: device.objectId,
                        html: device.objectId,
                        traduction: device.name
                    }
                    return pValue;
                })
        } else {
            return undefined
        }
    }

    predicatePossibleValuesCommandsGroup(elements: ElementParse[], suggestedTrad: string, otherTrad: string): GroupDataForm[] | undefined {
        if (arrayIsSet(elements)) {
            let groups: GroupDataForm[] = []
            const suggested = elements
                .filter(el => el.type != null && el.type.possibleCommands != null && objectIsSet(el.type.possibleCommands))
            const other = elements
                .filter(el => el.type == null || el.type.possibleCommands == null || !objectIsSet(el.type.possibleCommands))
            if (arrayIsSet(suggested)) {
                groups.push({
                    groupName: suggestedTrad,
                    possibleValues: suggested.map(el => {
                        const obj: dataForm = {
                            valueForm: el.objectId!,
                            html: el.name,
                            traduction: el.name,
                        }
                        return obj
                    })
                });
            }
            if (arrayIsSet(other)) {
                groups.push({
                    groupName: otherTrad,
                    possibleValues: other.map(el => {
                        const obj: dataForm = {
                            valueForm: el.objectId!,
                            html: el.name,
                            traduction: el.name,
                        }
                        return obj
                    })
                });
            }
            return groups
        }
        return undefined
    }

    predicatePossibleValuesCommand(element: Command): dataForm[] | undefined {
        if (element != null) {
            return Object.keys(element.possibleCommands)
                .map(pCommand => {
                    const pValue: dataForm = {
                        valueForm: pCommand,
                        html: pCommand,
                        traduction: 'possibleCommands.' + pCommand
                    }
                    return pValue;
                })
        } else {
            return undefined
        }
    }

    predicatePossibleValuesElementTypeCommand(element: ElementParse): dataForm[] | undefined {
        if (element != null && element.type && objectIsSet(element.type.possibleCommands)) {
            return Object.keys(element.type.possibleCommands)
                .map(pCommand => {
                    const pValue: dataForm = {
                        valueForm: pCommand,
                        html: pCommand,
                        traduction: 'possibleCommands.' + pCommand
                    }
                    return pValue;
                })
        } else {
            return undefined
        }
    }

    predicatePossibleValuesInfoName(device: ElementParse | undefined, sensorDataTypes: dataForm[] | undefined = undefined, suggestedTrad: string, otherTrad: string): GroupDataForm[] | undefined {
        if (device == null) {
            return undefined
        }
        let groups: GroupDataForm[] = []

        const periodicData = device?.type?.periodicData;
        let suggesteType: string[] = []

        if (periodicData != null) {
            suggesteType = Object.keys(periodicData)
        }
        if (arrayIsSet(suggesteType)) {
            groups.push({
                groupName: suggestedTrad,
                possibleValues: suggesteType.map(type => {
                    const obj: dataForm = {
                        valueForm: type,
                        html: type,
                        sensorEventTraduction: type,
                    }
                    return obj
                })
            });
        }
        if (arrayIsSet(sensorDataTypes)) {
            const notSuggesteType = sensorDataTypes?.filter(sType => !suggesteType.includes(sType.valueForm))
            if (arrayIsSet(notSuggesteType)) {
                groups.push({
                    groupName: otherTrad,
                    possibleValues: notSuggesteType.map(type => {
                        const obj: dataForm = {
                            valueForm: type.valueForm,
                            html: type.html,
                            traduction: type.traduction,
                        }
                        return obj
                    })
                });
            }
        }
        return arrayIsSet(groups) ? groups : undefined
    }

    predicatePossibleValuesTarget(device: ElementParse | undefined): dataForm[] | undefined {
        if (device != null) {
            const elementType = device.type;
            if (elementType?.possibleCommands != null) {
                const possibleCommands: any = elementType.possibleCommands
                return Object.keys(possibleCommands).map(key => {
                    const pValue: dataForm = {
                        valueForm: key,
                        html: key,
                        traduction: 'possibleCommands.' + key
                    }
                    return pValue;
                }
                )
            }
            return undefined
        } else {
            return undefined
        }
    }

    predicatePossibleValueType(sensorEventStatementType: SensorEventActuatorType): {
        traduction: string,
        valueForm: any
    }[] {
        let keys = Object.keys(sensorEventStatementType).filter(x => !(parseInt(x) >= 0));
        return keys.map(key => {
            return {
                traduction: 'SensorEvent.SensorEventActuatorType.' + (sensorEventStatementType as any)[key],
                valueForm: (sensorEventStatementType as any)[key]
            }
        })
    }

    getElementName(id: string) {
        if (id == null) {
            return undefined
        }
        const el = this.elements?.find(el => el.objectId == id)
        if (el) {
            return el.name
        }
        return undefined
    }

    /**
     * Funzione ricorsiva che cicla su tutte le chiavi di un oggetto
     * @param obj - L'oggetto principale da processare
     * @param callback - Funzione callback da eseguire per ogni chiave/valore
     * @param path - Il percorso corrente (per tracking dell'annidamento)
     */
    processObjectRecursively(
        obj: any,
        callback: (key: string, value: any, path: string) => void,
        path: string = ''
    ): void {
        if (obj == null || typeof obj !== 'object') {
            return;
        }

        Object.keys(obj).forEach(key => {
            const value = obj[key];
            const currentPath = path ? `${path}.${key}` : key;

            // Esegui la callback per la chiave corrente
            callback(key, value, currentPath);

            // Se il valore è un oggetto (ma non un array), ricorri
            if (value != null && typeof value === 'object' && !Array.isArray(value)) {
                this.processObjectRecursively(value, callback, currentPath);
            }
        });
    }

    /**
     * Helper per costruire la catena di .get() per il form Angular
     * @param keyToSet - La chiave base del form (es: 'configTypeLINEAR')
     * @param pathArray - Array delle chiavi dal path (es: ['p1', 'value', 'x'])
     * @returns Il FormControl corrispondente o undefined se non trovato
     */
    getFormControlByPath(keyToSet: string, pathArray: string[]): any {
        let formControl = this.form.get(keyToSet);

        for (const key of pathArray) {
            if (formControl) {
                formControl = formControl.get(key);
            } else {
                return undefined;
            }
        }

        return formControl;
    }

    /**
     * Helper per impostare un valore nel form usando il percorso
     * @param keyToSet - La chiave base del form (es: 'configTypeLINEAR')
     * @param pathArray - Array delle chiavi dal path (es: ['p1', 'value', 'x'])
     * @param value - Il valore da impostare
     */
    setFormValueByPath(keyToSet: string, pathArray: string[], value: any): void {
        const formControl = this.getFormControlByPath(keyToSet, pathArray);
        if (formControl) {
            formControl.setValue(value);
        } else {
            console.warn(`Form control not found for path: ${keyToSet}.${pathArray.join('.')}`);
        }
    }

    /**
     * Valida la configurazione del LinearActuator
     * @param config - La configurazione del LinearActuator da validare
     * @returns Oggetto con isValid (boolean) e errori (array di stringhe)
     */
    validateLinearActuatorConfig(config: any): { isValid: boolean, errors: string[] } {
        const errors: string[] = [];

        // Verifica campi obbligatori base
        if (!config) {
            errors.push('Configurazione mancante');
            return { isValid: false, errors };
        }

        // Verifica objectId (command)
        if (!stringIsSet(config.objectId)) {
            errors.push('ObjectId (command) è obbligatorio');
        }

        // Verifica command
        if (!stringIsSet(config.command)) {
            errors.push('Command è obbligatorio');
        }

        // Verifica x_sourceId
        if (!stringIsSet(config.x_sourceId)) {
            errors.push('X Source ID è obbligatorio');
        }

        // Verifica x_infoName
        if (!stringIsSet(config.x_infoName)) {
            errors.push('X Info Name è obbligatorio');
        }

        // Verifica P1
        if (!config.p1) {
            errors.push('P1 è obbligatorio');
        } else {
            const p1Errors = this.validatePoint(config.p1, 'P1');
            errors.push(...p1Errors);
        }

        // Verifica P2
        if (!config.p2) {
            errors.push('P2 è obbligatorio');
        } else {
            const p2Errors = this.validatePoint(config.p2, 'P2');
            errors.push(...p2Errors);
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Valida un singolo punto (P1 o P2)
     * @param point - Il punto da validare
     * @param pointName - Nome del punto per i messaggi di errore
     * @returns Array di errori
     */
    private validatePoint(point: any, pointName: string): string[] {
        const errors: string[] = [];

        if (!point) {
            errors.push(`${pointName} non può essere null o undefined`);
            return errors;
        }

        const hasValue = point.value && (
            point.value.x !== null && point.value.x !== undefined ||
            point.value.y !== null && point.value.y !== undefined
        );

        const hasSource = point.source && (
            (point.source.x && point.source.x.sensorId && point.source.x.infoName) ||
            (point.source.y && point.source.y.sensorId && point.source.y.infoName)
        );

        if (!hasValue && !hasSource) {
            errors.push(`${pointName} deve avere o 'value' o 'source' impostato`);
        }

        // Se ha value, verifica che i valori siano numeri
        if (hasValue && point.value) {
            if (point.value.x !== null && point.value.x !== undefined && typeof point.value.x !== 'number') {
                errors.push(`${pointName}.value.x deve essere un numero`);
            }
            if (point.value.y !== null && point.value.y !== undefined && typeof point.value.y !== 'number') {
                errors.push(`${pointName}.value.y deve essere un numero`);
            }
        }

        // Se ha source, verifica che sensorId e infoName siano stringhe non vuote
        if (hasSource && point.source) {
            if (point.source.x) {
                if (!point.source.x.sensorId || typeof point.source.x.sensorId !== 'string' || point.source.x.sensorId.trim() === '') {
                    errors.push(`${pointName}.source.x.sensorId è obbligatorio`);
                }
                if (!point.source.x.infoName || typeof point.source.x.infoName !== 'string' || point.source.x.infoName.trim() === '') {
                    errors.push(`${pointName}.source.x.infoName è obbligatorio`);
                }
            }
            if (point.source.y) {
                if (!point.source.y.sensorId || typeof point.source.y.sensorId !== 'string' || point.source.y.sensorId.trim() === '') {
                    errors.push(`${pointName}.source.y.sensorId è obbligatorio`);
                }
                if (!point.source.y.infoName || typeof point.source.y.infoName !== 'string' || point.source.y.infoName.trim() === '') {
                    errors.push(`${pointName}.source.y.infoName è obbligatorio`);
                }
            }
        }

        return errors;
    }

    /**
     * Valida la configurazione corrente del form LinearActuator
     * @returns Oggetto con isValid (boolean) e errori (array di stringhe)
     */
    validateCurrentLinearActuatorForm(): { isValid: boolean, errors: string[] } {
        const formValue = this.form.get('configTypeLINEAR')?.value;
        if (!formValue) {
            return { isValid: false, errors: ['Form LinearActuator non trovato'] };
        }

        // Converti il valore del form nel formato config appropriato
        const config = {
            objectId: formValue.objectId,
            command: formValue.command,
            x_sourceId: formValue.x_sourceId,
            x_infoName: formValue.x_infoName,
            minVal: formValue.minVal,
            maxVal: formValue.maxVal,
            p1: this.convertFormPointToConfig(formValue.p1),
            p2: this.convertFormPointToConfig(formValue.p2)
        };

        return this.validateLinearActuatorConfig(config);
    }

    /**
     * Converte un punto dal formato form al formato config
     * @param formPoint - Il punto dal form
     * @returns Il punto nel formato config
     */
    private convertFormPointToConfig(formPoint: any): any {
        if (!formPoint) return null;

        if (formPoint.type === PointsTypeValues.FIXED) {
            return { value: formPoint.value };
        } else if (formPoint.type === PointsTypeValues.FROM_SENSOR) {
            return { source: formPoint.source };
        }

        return null;
    }

    /**
     * Validator personalizzato per il form LinearActuator
     * @param control - Il FormGroup del configTypeLINEAR
     * @returns ValidationErrors o null se valido
     */
    linearActuatorValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null; // Se non c'è valore, lascia che i validators required si occupino dei singoli campi
        }

        // Converti il valore del form nel formato config
        const config = {
            objectId: control.value.objectId,
            command: control.value.command,
            x_sourceId: control.value.x_sourceId,
            x_infoName: control.value.x_infoName,
            minVal: control.value.minVal,
            maxVal: control.value.maxVal,
            p1: this.convertFormPointToConfig(control.value.p1),
            p2: this.convertFormPointToConfig(control.value.p2)
        };

        // Usa la funzione di validazione esistente
        const validationResult = this.validateLinearActuatorConfig(config);

        if (validationResult.isValid) {
            return null; // Valido
        } else {
            // Ritorna gli errori in formato Angular
            const errors: ValidationErrors = {};
            validationResult.errors.forEach((error, index) => {
                errors[`linearActuatorError_${index}`] = error;
            });
            return errors;
        }
    }

    /**
     * Validator personalizzato per un singolo punto (P1 o P2)
     * @param control - Il FormGroup del punto
     * @returns ValidationErrors o null se valido
     */
    pointValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null;
        }

        const formPoint = control.value;
        const configPoint = this.convertFormPointToConfig(formPoint);

        if (!configPoint) {
            return { pointRequired: 'Il punto deve avere value o source impostato' };
        }

        const errors = this.validatePoint(configPoint, 'Point');

        if (errors.length > 0) {
            const validationErrors: ValidationErrors = {};
            errors.forEach((error, index) => {
                validationErrors[`pointError_${index}`] = error;
            });
            return validationErrors;
        }

        return null;
    }



    protected readonly PointsTypeValues = PointsTypeValues;
}

